import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import NavbarWebsite from "../components/NavbarWebsite";
import NavbarFooter from "../components/Footer";

export const Route = createFileRoute("/pricing")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <>
      <NavbarWebsite />

      <div className=" max-w-3xl mx-auto md:mt-16 mt-10">
        <p className="text-4xl font-extrabold text-center mb-4 pt-9 ">
          Create your own learning materials
        </p>
        <p className="text-l font-normal text-center mb-4 pb-2 px-4">
          Get personalized learning that keeps you motivated and helps you
          remember faster.
        </p>
      </div>
      <div className="text-xs md:text-sm flex flex-row justify-center items-center gap-4 text-center pb-4 flex-wrap">
        <p className="rounded-full bg-gray-100 pt-2 pb-2 pl-3 pr-3">
          Free 14-day trial
        </p>
        <p className="rounded-full bg-gray-100 pt-2 pb-2 pl-3 pr-3">
          Change or cancel plan anytime
        </p>
      </div>

      <section className="w-full bg-white py-1 pb-10 md:py-6">
        <div className="mx-auto max-w-(--breakpoint-xl) px-4">
          <div className="flex flex-col md:flex-row md:justify-center md:items-stretch gap-6 flex-wrap">
            {/* Card 1: Starter */}
            <div className="w-full md:w-[380px] shrink-0 border bg-white border-slate-300 rounded-md text-left p-6 shadow-xs relative">
              <div className="absolute top-4 right-4 bg-green-400 text-white text-xs font-bold px-2 py-1 rounded-xs shadow-xs">
                Beginner
              </div>
              <div className="font-bold text-xl mb-2">Free Trial</div>
              <div className="text-gray-600 mb-4 md:h-[120px]">
                For the curious minds who want to explore language their own
                way. Experiment freely, find your rhythm, and learn through
                topics that truly interest you.
              </div>
              <div className="flex align-baseline items-end">
                <div className="pt-5 font-extrabold text-4xl">FREE</div>
                <div className="pt-5 pl-2 font-medium text-xl">for 14 days</div>
              </div>
              <Link
                to="/free_trial_signup"
                className="w-full px-6 py-2 mt-5 text-gray-900 text-lg outline-solid outline-1 rounded-md flex items-center justify-center"
                style={{ backgroundColor: "#F0F8FF" }}
              >
                🚀 Try for free
              </Link>
              <ul className="pt-4 text-sm text-gray-700 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>50 generated texts included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>100 translated words included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>Audio for 10 texts included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>Pronunciation audio for 500 words included</span>
                </li>
              </ul>
              <p className="pt-4 text-sm text-gray-700 space-y-">
                Try it free — no credit card, no auto-renewal.
              </p>
            </div>

            {/* Card 2: Classic */}
            <div className="w-full md:w-[380px] shrink-0 border bg-white border-slate-300 rounded-md text-left p-6 shadow-xs relative">
              <div className="absolute top-4 right-4 bg-green-400 text-white text-xs font-bold px-2 py-1 rounded-xs shadow-xs">
                Focused learners
              </div>
              <div className="font-bold text-xl mb-2">Pro</div>
              <div className="text-gray-600 mb-4 md:h-[120px]">
                For those who know what they want to achieve. Whether it's for
                work, everyday life or a specific goal. Get the content that
                truly moves you forward.
              </div>
              <div className="flex align-baseline items-end">
                <div className="pt-5 font-extrabold text-4xl">6,90 €</div>
                <div className="pt-5 pl-2 font-medium text-xl">per month</div>
              </div>
              <Link
                to="/start-learning"
                className="w-full px-6 py-2 mt-5 text-white text-lg bg-gray-900 rounded-md flex items-center justify-center"
              >
                Get started
              </Link>
              <ul className="pt-4 text-sm text-gray-700 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>200 generated texts included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>500 translated words included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>Audio for 50 texts included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>Pronunciation audio for 500 words included</span>
                </li>
              </ul>
              <p className="pt-4 text-sm text-gray-700 space-y-">
                Billed monthly – cancel anytime
              </p>
            </div>

            {/* Card 3: Learn Together */}
            <div className="w-full md:w-[380px] shrink-0 border bg-white border-slate-300 rounded-md text-left p-6 shadow-xs relative">
              {/* Badge in the top-right corner */}
              <div className="absolute top-4 right-4 bg-green-400 text-white text-xs font-bold px-2 py-1 rounded-xs shadow-xs animate-pulse">
                Study partners
              </div>

              <div className="font-bold text-xl mb-2">Learn together</div>
              <div className="text-gray-600 mb-4 md:h-[120px]">
                Double the motivation, half the cost. Perfect for
                language-learning duos, study groups, or small teams. Get 2 Pro
                Accounts for 4,90 € each!
              </div>

              <div className="flex align-baseline items-end">
                <div className="pt-5 font-extrabold text-4xl">9,80 €</div>
                <div className="pt-5 pl-2 font-medium text-xl">per month</div>
              </div>

              <Link
                to="/start-learning"
                className="w-full px-6 py-2 mt-5 text-white text-lg bg-gray-900 rounded-md flex items-center justify-center"
              >
                Get started together
              </Link>

              <ul className="pt-4 text-sm text-gray-700 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>200 generated texts included per account</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>500 translated words included per account</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>Audio for 50 texts included per account</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>
                    Pronunciation audio for 500 words included per account
                  </span>
                </li>
              </ul>
              <p className="pt-4 text-sm text-gray-700 space-y-">
                Billed monthly – cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>
      <NavbarFooter />
    </>
  );
}
